import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { parseAsUTC } from 'utils/date.utc';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { PageTitle } from 'ui/PageTitle';
import { Table } from 'ui/Table';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import SingleDateInput from 'pureUi/SingleDateInput';
import FluidButton from 'ui/FluidButton';
import { usePagination } from 'hooks/usePagination';
import { ESortOrder } from 'store/common/types';
import { AddConversionPairModal } from './AddConversionPairModal';
import { useConversionRatesListQuery, CONVERSION_RATES_QUERY_KEY } from './hooks/useConversionRatesQueries';
import { useDeleteConversionRate } from './hooks/useDeleteConversionRate';
import { ConfirmationModal, EConfirmationModalType } from 'ui/ConfirmationModal';
import { EExchangeRateSource, ISystemExchangeRateData } from './types';
import currencies from 'config/data/currencies.json';
import { CircleIconButton } from 'ui/CircleIconButton';
import ActivityIcon from 'ui/Icons/tabler/activity.component.svg';
import PencilIcon from 'ui/Icons/tabler/pencil.component.svg';
import TrashIcon from 'ui/Icons/tabler/trash.component.svg';

const currencyOptions: IMultiselectValueLabelPair[] = Object.keys(currencies).map(code => ({
  value: code,
  label: code,
}));

const getResultsText = (count: number): string => {
  if (!count) return '(no results found)';
  if (count === 1) return '(1 result found)';
  return `(${count} results found)`;
};

const getStrategyLabel = (source: EExchangeRateSource, markupPercentage: number): string => {
  if (source === 'fixed') return 'Fixed Rate';
  if (markupPercentage !== 0) return 'Mark up %';
  return 'API Rate';
};

const getValueDisplay = (source: EExchangeRateSource, markupPercentage: number, value: number): string => {
  if (source === 'fixed') return String(value);
  if (markupPercentage !== 0) return String(markupPercentage);
  return '---';
};

const useQueryParams = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const setSearchParams = useCallback(
    (newParams: URLSearchParams, options?: { replace?: boolean }) => {
      const newSearch = newParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      if (options?.replace) {
        history.replace(newUrl);
      } else {
        history.push(newUrl);
      }
    },
    [history, location.pathname]
  );

  return [searchParams, setSearchParams] as const;
};

const tableColumns: ITableColumn[] = [
  { id: 'conversion', label: 'Conversion', width: '120px', fieldToSortBy: 'currency' },
  { id: 'apiRate', label: 'API Rate', width: '140px' },
  { id: 'buyStrategy', label: 'Buy Strategy', width: '120px', className: 'border-l border-gray-20' },
  { id: 'buyValue', label: 'Value', width: '110px' },
  { id: 'buyEffective', label: 'Effective', width: '120px', className: 'border-r border-gray-20' },
  { id: 'sellStrategy', label: 'Sell Strategy', width: '120px', className: 'border-l border-gray-20' },
  { id: 'sellValue', label: 'Value', width: '110px' },
  { id: 'sellEffective', label: 'Effective', width: '120px', className: 'border-r border-gray-20' },
  { id: 'actions', label: '', width: '60px' },
];

const tableColumnsWithoutActions: ITableColumn[] = tableColumns.filter(col => col.id !== 'actions');

export const ConversionRatesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ISystemExchangeRateData | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ISystemExchangeRateData | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isAdmin = useSelector(AuthSelectors.isAdmin);
  const isFinance = useSelector(AuthSelectors.isFinanceUser);
  const canManage = isAdmin || isFinance;

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useQueryParams();

  const today = parseAsUTC(new Date().toISOString());

  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  
  let dateParam = searchParams.get('date') || today;
  if(dateParam > today) dateParam = today;

  const isCurrentDate = dateParam === today;
  const showActions = canManage && isCurrentDate;

  const paginationData = usePagination({ initialPage: 1, initialPerPage: 10, initialTotal: 0 });

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<ESortOrder>(ESortOrder.DESC);

  const { mutate: deleteConversionRate, isPending: isDeleting } = useDeleteConversionRate();

  const filters = useMemo(
    () => ({
      from: fromParam || null,
      to: toParam || null,
      date: dateParam,
    }),
    [fromParam, toParam, dateParam]
  );

  const { data, isLoading, isError, isFetching } = useConversionRatesListQuery(
    paginationData.page,
    paginationData.perPage,
    sortBy,
    sortOrder,
    filters
  );

  useEffect(() => {
    if (data) {
      setHasLoadedOnce(true);
      paginationData.setTotal(data.meta.total);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: [CONVERSION_RATES_QUERY_KEY] });
    };
  }, [queryClient]);

  // Sync pagination page to URL
  useEffect(() => {
    const currentPageParam = searchParams.get('page');
    const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1;
    if (paginationData.page !== currentPage) {
      const newParams = new URLSearchParams(searchParams);
      if (paginationData.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', String(paginationData.page));
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [paginationData.page]);

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    },
    [searchParams, setSearchParams, paginationData]
  );

  const handleFromCurrencyChange = useCallback(
    (values: string[]) => {
      updateFilter('from', values[0] || null);
    },
    [updateFilter]
  );

  const handleToCurrencyChange = useCallback(
    (values: string[]) => {
      updateFilter('to', values[0] || null);
    },
    [updateFilter]
  );

  const handleDateChange = useCallback(
    (date: Date | null) => {
      updateFilter('date', date ? parseAsUTC(date.toISOString()) : null);
    },
    [updateFilter]
  );

  const handleSort = useCallback((newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder as ESortOrder);
  }, []);

  const items: ISystemExchangeRateData[] = data?.data || [];
  const total = data?.meta.total || 0;

  const handleRequestDelete = useCallback((item: ISystemExchangeRateData) => {
    setItemToDelete(item);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!itemToDelete) return;
    deleteConversionRate({ from: itemToDelete.from, to: itemToDelete.to });
    setItemToDelete(null);
  }, [deleteConversionRate, itemToDelete]);

  const handleCancelDelete = useCallback(() => {
    setItemToDelete(null);
  }, []);

  const tableRows: ITableRow[] = items.map(item => ({
    id: item.uuid,
    cells: [
      {
        id: 'conversion',
        value: (
          <span className="flex items-center gap-[5px]">
            <span className="bg-yellow-20 rounded-[10px] py-[5px] px-[3px] text-center min-w-[36px]">
              <span className="font-pt-sans text-[13px] text-black">{item.from}</span>
            </span>
            <span className="text-gray-100">→</span>
            <span className="bg-green-25 rounded-[10px] py-[5px] px-[3px] text-center min-w-[36px]">
              <span className="font-pt-sans text-[13px] text-black">{item.to}</span>
            </span>
          </span>
        ),
      },
      {
        id: 'apiRate',
        value: (
          <div className="inline-flex items-center gap-[5px] bg-blue-10 rounded-[3px] px-[3px] py-[3px] min-w-[120px] max-w-[120px]">
            <ActivityIcon className="w-[18px] h-[18px] text-status-enquiry flex-shrink-0" />
            <span className="text-[13px] font-pt-sans text-status-enquiry truncate">{item.api}</span>
          </div>
        ),
      },
      {
        id: 'buyStrategy',
        value: getStrategyLabel(item.buySource, item.buyMarkupPercentage),
        className: 'border-l border-gray-20',
      },
      { id: 'buyValue', value: getValueDisplay(item.buySource, item.buyMarkupPercentage, item.buy) },
      {
        id: 'buyEffective',
        value: <span className="font-bold">{item.effectiveBuy}</span>,
        className: 'border-r border-gray-20',
      },
      {
        id: 'sellStrategy',
        value: getStrategyLabel(item.sellSource, item.sellMarkupPercentage),
        className: 'border-l border-gray-20',
      },
      { id: 'sellValue', value: getValueDisplay(item.sellSource, item.sellMarkupPercentage, item.sell) },
      {
        id: 'sellEffective',
        value: <span className="font-bold">{item.effectiveSell}</span>,
        className: 'border-r border-gray-20',
      },
      ...(showActions
        ? [
            {
              id: 'actions',
              value: (
                <span className="flex items-center justify-center gap-[10px]">
                  <CircleIconButton type="secondary" IconComponent={PencilIcon} onClick={() => setEditingItem(item)} />
                  <CircleIconButton
                    type="secondary"
                    IconComponent={TrashIcon}
                    onClick={() => handleRequestDelete(item)}
                    disabled={isDeleting}
                  />
                </span>
              ),
            },
          ]
        : []),
    ],
  }));

  const visibleColumns = showActions ? tableColumns : tableColumnsWithoutActions;

  return (
    <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
      <PageTitle
        primaryText="Conversion Rates"
        secondaryCondition={hasLoadedOnce && !isLoading}
        secondaryText={getResultsText(total)}
        loadingCondition={isFetching && hasLoadedOnce}
        loadingText="Loading ..."
      />

      {isError && <ErrorBar />}

      {!isError && !hasLoadedOnce && <LoadingBar />}

      {!isError && hasLoadedOnce && (
        <>
          <div className="mb-5">
            <span className="block font-pt-sans text-[13px] text-black mb-3 font-bold">Filter Results</span>

            <div className="flex flex-wrap gap-5 items-end">
              <div className="flex flex-col w-[160px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">From Currency</span>
                <Multiselect
                  className="bg-ivory"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={currencyOptions}
                  selectedValues={fromParam ? [fromParam] : []}
                  onUpdate={handleFromCurrencyChange}
                  isSingleSelectMode
                  hideCheckboxes
                  isCloseOnSelect
                  isIncludeClearButton
                  placeholderText="All"
                  isEnableFuzzySearch
                />
              </div>

              <div className="flex flex-col w-[160px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">To Currency</span>
                <Multiselect
                  className="bg-ivory"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={currencyOptions}
                  selectedValues={toParam ? [toParam] : []}
                  onUpdate={handleToCurrencyChange}
                  isSingleSelectMode
                  hideCheckboxes
                  isCloseOnSelect
                  isIncludeClearButton
                  placeholderText="All"
                  isEnableFuzzySearch
                />
              </div>

              <div className="flex flex-col w-[140px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Date</span>
                <SingleDateInput
                  buttonClassName="!normal-case !h-[35px] !bg-ivory !border-gray-40"
                  displayStringClassName="!font-pt-sans"
                  value={dateParam ? new Date(dateParam + 'T00:00:00') : new Date()}
                  onChange={handleDateChange}
                  enablePastDates={true}
                  placeholder="Select date..."
                  placeholderClassName="italic text-gray-100"
                  calendarCount={1}
                  maxDate={new Date()}
                />
              </div>

              {canManage && (
                <div className="ml-auto">
                  <FluidButton type="primary" onClick={() => setIsModalOpen(true)}>
                    + New Currency Pair
                  </FluidButton>
                </div>
              )}
            </div>
          </div>

          <Table
            columns={visibleColumns}
            rows={tableRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={isFetching}
            messageWhenEmpty="No Results"
            className="mt-5 border-l border-r border-gray-20"
            evenColor="white-hard"
          />

          {tableRows.length >= 1 && (
            <Pagination
              className="mt-5"
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}

      {itemToDelete && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          title="You are going to remove a Currency Pair."
          message="Are you sure?"
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {isModalOpen && <AddConversionPairModal onClose={() => setIsModalOpen(false)} />}
      {editingItem && <AddConversionPairModal editData={editingItem} onClose={() => setEditingItem(null)} />}
    </div>
  );
};
